import { booleanAttribute, Directive, inject, Input, numberAttribute, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { MngDialogService } from '../dialog/mng-dialog.service';
import { DIALOG_RESULT_YES } from '../dialog/mng-dialog.types';
import { Hotkey, MngHotkeysService } from '../hotkeys/mng-hotkeys.service';
import { MngActionDef } from '../mng-actiondef';
import { ThemePalette } from '@angular/material/core';
import { TooltipPosition } from '@angular/material/tooltip';
import { MngColor } from '@mng/mng-icon';

@Directive()
export abstract class MngBaseButtonComponent implements OnInit, OnDestroy {
  private _preventAction = false;

  private _buttonHotkeys: string[] = [];

  @Input({ required: true })
  actionDef: MngActionDef;

  @Input()
  actionArg: any;

  /**
   * @deprecated no usage
   */
  @Input()
  titleArg: any;

  /**
   * @deprecated no usage
   */
  @Input()
  colorFunctionArg: any;

  /**
   * @deprecated use 'actionArg' instead
   */
  @Input()
  iconFunctionArg: any;

  /**
   * @deprecated use 'actionArg' instead
   */
  @Input()
  visibleFunctionArg: any;

  /**
   * @deprecated use 'actionArg' instead
   */
  @Input()
  disabledFunctionArg: any;

  @Input()
  color: 'primary' | 'accent' | 'error' | 'warning' | '';

  @Input({ transform: numberAttribute })
  fontSize = 24;

  @Input({ transform: booleanAttribute })
  buttonTooltip = false;

  @Input({ transform: numberAttribute })
  tooltipDelay = 200;

  @Input()
  tooltipPosition: TooltipPosition = 'below';

  protected readonly sanitizer = inject(DomSanitizer);
  protected readonly dialogService = inject(MngDialogService);
  protected readonly hotkeysService = inject(MngHotkeysService);

  ngOnInit() {
    if (this.actionDef.hotkey) {
      const arg = this.getSpecificOrActionArg(this.titleArg);
      const hotkey: Partial<Hotkey> = {
        keys: this.actionDef.hotkey.replace('+', '.'),
        description: this.actionDef.getTitle(arg),
        callback: () => {
          if (this.isVisible && !this.isDisabled) {
            this.executeAction();
          }
        }
      };

      this.hotkeysService.register(hotkey);
      this._buttonHotkeys.push(hotkey.keys);
    }

    if (this.actionDef.menu) {
      this.actionDef.menu
        .filter((ad: MngActionDef) => ad.hotkey)
        .forEach((menuActionDef: MngActionDef) => {
          const arg = this.getSpecificOrActionArg(this.titleArg);
          const hotkey: Partial<Hotkey> = {
            keys: menuActionDef.hotkey,
            description: menuActionDef.getTitle(arg),
            callback: () => {
              if (menuActionDef.isVisible() && !menuActionDef.isDisabled()) {
                this.callAction(menuActionDef);
              }
            }
          };
          this.hotkeysService.register(hotkey);
          this._buttonHotkeys.push(hotkey.keys);
        });
    }
  }

  ngOnDestroy() {
    if (this._buttonHotkeys.length > 0) {
      this._buttonHotkeys.forEach((hotkey) => {
        this.hotkeysService.unregister(hotkey);
      });
    }
  }

  sanitizeStyle(html: string): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(html);
  }

  sanitizeHtml(html: string): SafeStyle {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  executeAction(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }

    if (this.actionDef.action) {
      if (this.actionDef.dialog) {
        this.dialogService
          .showYesNo({
            title: this.actionDef.dialogTitle,
            iconName: this.actionDef.dialogIconName,
            htmlContent: this.actionDef.dialogHtmlContent,
            yesText: this.actionDef.dialogYesButtonText,
            noText: this.actionDef.dialogNoButtonText
          })
          .afterClosed()
          .toPromise()
          .then((answer) => {
            if (answer === DIALOG_RESULT_YES) {
              this.callAction(this.actionDef);
            }
          });
      } else {
        this.callAction(this.actionDef);
      }
    } else if (this.actionDef.externalUrl) {
      window.open(this.actionDef.externalUrl, this.actionDef.externalUrlTarget);
    }
  }

  executeMenuAction(actionDef: MngActionDef, index?: number) {
    if (actionDef.action) {
      if (actionDef.dialog) {
        this.dialogService
          .showYesNo({
            title: actionDef.dialogTitle,
            iconName: actionDef.dialogIconName,
            htmlContent: actionDef.dialogHtmlContent,
            yesText: this.actionDef.dialogYesButtonText,
            noText: this.actionDef.dialogNoButtonText
          })
          .afterClosed()
          .toPromise()
          .then((answer) => {
            if (answer === DIALOG_RESULT_YES) {
              this.callAction(actionDef, index);
            }
          });
      } else {
        this.callAction(actionDef, index);
      }
    } else if (actionDef.externalUrl) {
      window.open(actionDef.externalUrl, actionDef.externalUrlTarget);
    }
  }

  private callAction(actionDef: MngActionDef, index?: number) {
    if (!this._preventAction) {
      this._preventAction = true;
      setTimeout(() => {
        this._preventAction = false;
      }, 1000);

      if (index !== null && index !== undefined) {
        if (this.actionArg !== null && this.actionArg !== undefined) {
          actionDef.action(this.actionArg, index);
        } else {
          actionDef.action(index);
        }
      } else {
        if (this.actionArg !== null && this.actionArg !== undefined) {
          actionDef.action(this.actionArg);
        } else {
          actionDef.action();
        }
      }
    }
  }

  getButtonId(): string {
    return this.actionDef.id;
  }

  getTooltipMessage() {
    if (this.buttonTooltip || this.actionDef._showTooltip) {
      return this.actionDef.tooltip;
    }
  }

  getDisabledTooltipMessage() {
    if (this.actionDef.disabledTooltip) {
      const arg = this.getSpecificOrActionArg(this.disabledFunctionArg);
      if (arg !== undefined) {
        return this.actionDef.disabledTooltip(arg);
      } else {
        return this.actionDef.disabledTooltip();
      }
    }
  }

  get buttonColor(): ThemePalette {
    if (this.color) {
      return <ThemePalette>this.color;
    } else {
      const arg = this.getSpecificOrActionArg(this.colorFunctionArg);
      if (arg !== undefined) {
        return <ThemePalette>this.actionDef.getColor(arg);
      } else {
        return <ThemePalette>this.actionDef.getColor();
      }
    }
  }

  get iconColor(): MngColor {
    const arg = this.getSpecificOrActionArg(this.iconFunctionArg);
    if (arg !== undefined) {
      return this.actionDef.getIconColor(arg);
    } else {
      return this.actionDef.getIconColor();
    }
  }

  get iconName(): string {
    const arg = this.getSpecificOrActionArg(this.iconFunctionArg);
    if (arg !== undefined) {
      return this.actionDef.getIconName(arg);
    } else {
      return this.actionDef.getIconName();
    }
  }

  get isVisible(): boolean {
    const arg = this.getSpecificOrActionArg(this.visibleFunctionArg);
    if (arg !== undefined) {
      return this.actionDef.isVisible(arg);
    } else {
      return this.actionDef.isVisible();
    }
  }

  get isDisabled(): boolean {
    const arg = this.getSpecificOrActionArg(this.disabledFunctionArg);
    if (arg !== undefined) {
      return this.actionDef.isDisabled(arg);
    } else {
      return this.actionDef.isDisabled();
    }
  }

  private getSpecificOrActionArg(specificArg: any): any {
    if (specificArg !== null && specificArg !== undefined) {
      return specificArg;
    }

    if (this.actionArg !== null && this.actionArg !== undefined) {
      return this.actionArg;
    }

    return undefined;
  }

  get text(): string {
    const arg = this.getSpecificOrActionArg(this.titleArg);
    return this.actionDef.getText(arg).toUpperCase();
  }
}
